var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { Type } from "class-transformer";
import { IsArray, IsEnum, IsOptional, IsString, ValidateNested } from "class-validator";
import { StudyTranslationLanguage } from "./study-translation-language.dtos";
import { filterFalse, filterUndefined } from "./utils";
export var PreviewConsentMode;
(function (PreviewConsentMode) {
    PreviewConsentMode["REMOTE"] = "REMOTE";
    PreviewConsentMode["GUIDED"] = "GUIDED";
})(PreviewConsentMode || (PreviewConsentMode = {}));
export var PreviewConsentFlow;
(function (PreviewConsentFlow) {
    PreviewConsentFlow["PATIENT_DIRECTED_DATA_EXCHANGE"] = "PATIENT_DIRECTED_DATA_EXCHANGE";
})(PreviewConsentFlow || (PreviewConsentFlow = {}));
export var PreviewParticipantType;
(function (PreviewParticipantType) {
    PreviewParticipantType["ADULT"] = "ADULT";
    PreviewParticipantType["MINOR"] = "MINOR";
})(PreviewParticipantType || (PreviewParticipantType = {}));
export var PreviewRequestType;
(function (PreviewRequestType) {
    PreviewRequestType["INITIAL_HEALTH_RECORD_ACCESS"] = "INITIAL_HEALTH_RECORD_ACCESS";
    PreviewRequestType["HEALTH_RECORD_UPDATE"] = "HEALTH_RECORD_UPDATE";
})(PreviewRequestType || (PreviewRequestType = {}));
export class PreviewMetadata {
    constructor(fields) {
        Object.assign(this, fields);
        this.key = getPreviewKey(this);
    }
}
__decorate([
    IsEnum(PreviewConsentMode)
], PreviewMetadata.prototype, "consentMode", void 0);
__decorate([
    IsEnum(PreviewConsentFlow)
], PreviewMetadata.prototype, "consentFlow", void 0);
__decorate([
    IsEnum(StudyTranslationLanguage)
], PreviewMetadata.prototype, "language", void 0);
__decorate([
    IsEnum(PreviewParticipantType),
    IsOptional()
], PreviewMetadata.prototype, "participantType", void 0);
__decorate([
    IsEnum(PreviewRequestType),
    IsOptional()
], PreviewMetadata.prototype, "requestType", void 0);
__decorate([
    IsString()
], PreviewMetadata.prototype, "key", void 0);
export class PreviewMetadataInput {
    constructor(fields) {
        Object.assign(this, fields);
    }
}
__decorate([
    Type(() => PreviewMetadata),
    IsArray(),
    ValidateNested({ each: true })
], PreviewMetadataInput.prototype, "previewMetadata", void 0);
const previewKeySeparator = ":";
export function getPreviewKey(previewMetadata) {
    const { consentMode, consentFlow, language, requestType } = previewMetadata;
    return filterUndefined([consentMode, consentFlow, language, requestType]).join(previewKeySeparator);
}
export function previewKeyToPathName(key) {
    if (!key)
        return null;
    return key.toLowerCase().replace(new RegExp(`${previewKeySeparator}`, "g"), "-");
}
export function generatePreviewList(opts, isFullList = false) {
    const consentModes = [PreviewConsentMode.REMOTE, opts.includeGuided && PreviewConsentMode.GUIDED];
    const consentFlows = [PreviewConsentFlow.PATIENT_DIRECTED_DATA_EXCHANGE];
    const requestTypes = [
        opts.includeRequestTypes ? PreviewRequestType.INITIAL_HEALTH_RECORD_ACCESS : false,
        opts.includeRequestTypes ? PreviewRequestType.HEALTH_RECORD_UPDATE : false,
        !opts.includeRequestTypes || isFullList ? undefined : false,
    ];
    return opts.languages.flatMap((language) => {
        return filterFalse(consentModes).flatMap((consentMode) => {
            return filterFalse(consentFlows).flatMap((consentFlow) => {
                return filterFalse(requestTypes).flatMap((requestType) => {
                    return new PreviewMetadata({
                        consentMode,
                        consentFlow,
                        language,
                        requestType,
                    });
                });
            });
        });
    });
}
const previewMetadataList = generatePreviewList({
    languages: Object.values(StudyTranslationLanguage),
    includeGuided: true,
    includeRequestTypes: true,
}, true);
export function getPreviewKeys(previewMetadata) {
    return new Set(previewMetadata.map((metadata) => metadata.key).filter((key) => key !== undefined));
}
export const previewKeys = getPreviewKeys(previewMetadataList);
export function isPreviewKeyValid(key) {
    return !!key && previewKeys.has(key);
}
export function filterValidPreviewList(previewMetadata) {
    return previewMetadata
        .map((metadata) => {
        metadata.key = getPreviewKey(metadata);
        return metadata;
    })
        .filter((metadata) => isPreviewKeyValid(metadata.key));
}
